import { useEffect, useState } from "react";
import { QrCode } from "lucide-react";
import { registrationAPI } from "@/services/api";

interface CheckInPassProps {
  eventId: string;
}

/**
 * The member's own QR pass. An admin scans it at the door to mark attendance.
 * Renders nothing when the member has no active registration for this event.
 */
export default function CheckInPass({ eventId }: CheckInPassProps) {
  const [qr, setQr] = useState<string | null>(null);
  const [checkedInAt, setCheckedInAt] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    registrationAPI
      .getCheckInPass(eventId)
      .then((data) => {
        if (cancelled) return;
        setQr(data.qrDataUrl || null);
        setCheckedInAt(data.checkedInAt || null);
      })
      .catch(() => {
        // No registration for this event, or not signed in — hide the pass.
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  if (unavailable || !qr) return null;

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2 text-sm font-medium text-secondary transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <QrCode className="h-4 w-4" />
        {open ? "Hide entry pass" : "Show entry pass"}
      </button>

      {open && (
        <div className="mt-3 inline-block rounded-lg border border-border bg-white p-4 text-center">
          <img
            src={qr}
            alt="Your check-in code"
            width={180}
            height={180}
            className="mx-auto"
          />
          <p className="mt-3 max-w-[180px] text-xs text-gray-500">
            {checkedInAt
              ? "You're already checked in."
              : "Show this at the door to check in."}
          </p>
        </div>
      )}
    </div>
  );
}
